import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import Modal from "@/components/Modal";
import ContextButton from "@/components/ContextButton";
import { useLocalContext, describeScope } from "@/lib/moduleContext";
import { useDemoMode } from "@/lib/demoMode";
import { GEO_SCALE_OPTIONS, geoLabel } from "@/lib/geoScale";
import {
  Coins, Sparkles, Loader2, Plus, Edit2, Trash2, X, Check, ChevronDown, ChevronUp,
  Building2, Users, AlertTriangle, RefreshCw, ArrowRight, Layers, Target, TrendingUp,
  FileDown, Crown, Waves,
} from "lucide-react";
import BrasArmeAnalysisPanel from "@/components/BrasArmeAnalysisPanel";
import ValueChainRiver from "@/components/ValueChainRiver";
import OriginationCausalFractal from "@/components/OriginationCausalFractal";
import { ActifsProvider, OriginationFractaleAscendante } from "@/pages/saas/OriginationExtensions";
import PitchCaptureButton from "@/components/PitchCaptureButton";
import { notify } from "@/lib/notify";
import { PersonaAction } from "@/lib/persona";
import ThreeQPageHeader from "@/components/ThreeQPageHeader";

const LEVEL_COLORS = {
  fonds_propres: "#1D9E75",
  dette_senior: "#378ADD",
  dette_mezzanine: "#7F77DD",
  capital_investissement: "#EF9F27",
  marches: "#E24B4A",
};

function fmtEur(n) {
  if (!Number.isFinite(n)) return "—";
  if (n >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(1)} Md€`;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)} M€`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)} k€`;
  return `${n} €`;
}

function fmtErr(d) {
  if (!d) return "Erreur.";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(d);
}

function friendlyFractaleError(d) {
  const kind = d?.error_kind;
  if (kind === "budget_exceeded") {
    return "Budget Universal Key épuisé. Rechargez votre clé dans Profil → Universal Key → Add Balance, puis relancez la génération.";
  }
  if (kind === "timeout") {
    return "NOAM n'a pas répondu à temps. Réessayez dans quelques instants — la charge LLM est probablement élevée.";
  }
  if (kind === "upstream") {
    return "Le service NOAM est temporairement indisponible (erreur upstream). Réessayez dans 1-2 minutes.";
  }
  return d?.error || "La génération a échoué.";
}

export default function SourcesCapital() {
  const isDemo = useDemoMode();
  const [brasArmeOpen, setBrasArmeOpen] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [editingActor, setEditingActor] = useState(null);
  const [generatingAi, setGeneratingAi] = useState(false);
  const [tab, setTab] = useState("fractale");
  const [fractale, setFractale] = useState(null);
  const [fractaleError, setFractaleError] = useState(null);
  const [generatingFractale, setGeneratingFractale] = useState(false);
  const [fractaleElapsed, setFractaleElapsed] = useState(0);
  const pollTimerRef = useRef(null);
  const elapsedTimerRef = useRef(null);
  const pollStartedAtRef = useRef(null);
  const [scope] = useLocalContext("origination");

  const stopPolling = () => {
    if (pollTimerRef.current) { clearInterval(pollTimerRef.current); pollTimerRef.current = null; }
    if (elapsedTimerRef.current) { clearInterval(elapsedTimerRef.current); elapsedTimerRef.current = null; }
    pollStartedAtRef.current = null;
    setFractaleElapsed(0);
  };

  const startPolling = () => {
    if (pollTimerRef.current) return;
    pollStartedAtRef.current = Date.now();
    setFractaleElapsed(0);
    setGeneratingFractale(true);
    elapsedTimerRef.current = setInterval(() => {
      if (pollStartedAtRef.current) {
        setFractaleElapsed(Math.floor((Date.now() - pollStartedAtRef.current) / 1000));
      }
    }, 1000);
    pollTimerRef.current = setInterval(async () => {
      try {
        const r = await api.get("/workspace/origination/fractale");
        const d = r.data || {};
        // Bail out after 5 minutes
        const elapsed = pollStartedAtRef.current ? (Date.now() - pollStartedAtRef.current) / 1000 : 0;
        if (elapsed > 300) {
          stopPolling();
          setGeneratingFractale(false);
          setFractaleError("Délai dépassé (5 min). La génération continue en arrière-plan — rafraîchissez la page dans 1-2 min pour voir le résultat. Si le problème persiste, contactez le support.");
          return;
        }
        if (d.status === "ok") {
          setFractale(d);
          setFractaleError(null);
          stopPolling();
          setGeneratingFractale(false);
        } else if (d.status === "error") {
          const friendly = friendlyFractaleError(d);
          setFractaleError(friendly);
          stopPolling();
          setGeneratingFractale(false);
        }
        // else status === 'generating' (or undefined) → keep polling
      } catch (e) {
        // Transient error, keep polling but record
        const detail = e?.response?.data?.detail;
        if (detail) setFractaleError(fmtErr(detail));
      }
    }, 4000);
  };

  const load = async () => {
    try { const r = await api.get("/workspace/origination-capital"); setData(r.data); }
    catch (e) { setError(fmtErr(e?.response?.data?.detail)); }
  };
  const loadFractale = async () => {
    try {
      const r = await api.get("/workspace/origination/fractale");
      const d = r.data || {};
      setFractale(d);
      // Resume polling if a generation is already running (e.g. user refreshed the page)
      if (d.status === "generating") {
        startPolling();
      } else if (d.status === "error") {
        // Persist friendly error on reload so user keeps the recovery hint
        setFractaleError(friendlyFractaleError(d));
      }
    }
    catch (e) { setFractaleError(fmtErr(e?.response?.data?.detail)); }
  };
  // Mount-only — load() and loadFractale() are stable closures over `api`,
  // and re-running them on every render would loop. stopPolling is the
  // cleanup callback executed on unmount.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); loadFractale(); return stopPolling; }, []);

  const generateAi = async () => {
    setGeneratingAi(true); setError(null);
    try {
      const r = await api.post("/workspace/origination-capital/ai-reading");
      if (r.data?.status === "accepted") {
        // V23.16.x — génération asynchrone : on notifie et on poll le résultat
        const prevAt = data?.ai_reading_at || null;
        notify.success("Lecture IA lancée — sources de capital et formes de deal personnalisées en cours de génération (~1 min). Vous pouvez naviguer.");
        const startedAt = Date.now();
        const iv = setInterval(async () => {
          try {
            const rr = await api.get("/workspace/origination-capital");
            if (rr.data?.ai_reading_at && rr.data.ai_reading_at !== prevAt) {
              setData(rr.data);
              clearInterval(iv);
              setGeneratingAi(false);
            } else if (Date.now() - startedAt > 240000) {
              clearInterval(iv);
              setGeneratingAi(false);
            }
          } catch { /* keep polling */ }
        }, 5000);
        return;
      }
      await load();
      setGeneratingAi(false);
    } catch (e) {
      setError(fmtErr(e?.response?.data?.detail));
      setGeneratingAi(false);
    }
  };

  const generateFractale = async () => {
    setFractaleError(null);
    setGeneratingFractale(true);
    try {
      const r = await api.post("/workspace/origination/fractale/generate", {
        context_hint: describeScope(scope),
      });
      // Backend returns 202 { status: 'accepted', job_id, message }.
      // Start polling for completion.
      if (r.data?.status === "accepted") {
        startPolling();
      } else if (r.data?.levels?.length === 5) {
        // Synchronous response (legacy / cached)
        setFractale(r.data);
        setGeneratingFractale(false);
      } else {
        startPolling();
      }
    }
    catch (e) {
      setFractaleError(fmtErr(e?.response?.data?.detail));
      setGeneratingFractale(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="sources-capital-root">
      <ThreeQPageHeader endpoint="/workspace/sources-capital/top-priority" />
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-[#1D9E75] noam-overline"><Coins className="w-3 h-3" /> Stratégique · Origination</div>
          <h1 className="noam-display text-3xl font-bold tracking-tight mt-1">Origination</h1>
          <p className="text-zinc-400 text-sm mt-2 max-w-3xl">D'où vous venez, et donc où vous pouvez aller. Cartographie fractale Pareto en 5 niveaux, et capital pour financer chaque niveau.</p>
        </div>
        {!isDemo && (
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={() => setBrasArmeOpen(true)} className="btn-secondary text-sm" data-testid="sources-bras-arme-btn" title="Identifier les Super Partenaires et Super Clients (grandes institutions qui peuvent vous déléguer une fonction opérationnelle ou structurante).">
              <Crown className="w-4 h-4 text-[#EFD127]" /> Super Partenaires & Clients
            </button>
            <ContextButton moduleKey="origination" />
          </div>
        )}
      </header>

      <BrasArmeAnalysisPanel open={brasArmeOpen} onClose={() => setBrasArmeOpen(false)} sourceModule="sources_capital" />

      <div className="flex items-center gap-1 border-b border-white/5 overflow-x-auto" data-testid="origination-tabs">
        <TabButton active={tab === "fractale"} onClick={() => setTab("fractale")} icon={Layers} testid="tab-fractale">
          Mon marché en fractale
        </TabButton>
        <TabButton active={tab === "causale"} onClick={() => setTab("causale")} icon={Layers} testid="tab-causale">
          Fractale causale
        </TabButton>
        <TabButton active={tab === "ascendante"} onClick={() => setTab("ascendante")} icon={ChevronUp} testid="tab-ascendante">
          Fractale ascendante
        </TabButton>
        <TabButton active={tab === "riviere"} onClick={() => setTab("riviere")} icon={Waves} testid="tab-riviere">
          Vue rivière
        </TabButton>
        <TabButton active={tab === "capital"} onClick={() => setTab("capital")} icon={Coins} testid="tab-capital">
          Capital de financement
        </TabButton>
      </div>

      {tab === "fractale" && (
        <FractaleView
          fractale={fractale}
          error={fractaleError}
          isDemo={isDemo}
          generating={generatingFractale}
          elapsed={fractaleElapsed}
          onGenerate={generateFractale}
        />
      )}

      {tab === "causale" && (
        <div data-testid="origination-causale-tab">
          <OriginationCausalFractal />
        </div>
      )}

      {tab === "ascendante" && (
        <ActifsProvider>
          <OriginationFractaleAscendante />
        </ActifsProvider>
      )}

      {tab === "riviere" && (
        <RiviereTab />
      )}

      {tab === "capital" && (
        <CapitalCascadeView
          data={data}
          error={error}
          isDemo={isDemo}
          generatingAi={generatingAi}
          onAddActor={() => setEditingActor({})}
          onGenerateAi={generateAi}
          fractale={fractale}
          onReload={load}
        />
      )}

      {editingActor !== null && (
        <ActorEditModal
          actor={editingActor}
          onClose={() => setEditingActor(null)}
          onSaved={() => { setEditingActor(null); load(); }}
        />
      )}
    </div>
  );
}

function TabButton({ active, onClick, icon: Icon, testid, children }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`px-4 py-2.5 text-xs font-medium inline-flex items-center gap-2 border-b-2 transition-colors -mb-px ${
        active
          ? "border-[#1D9E75] text-white"
          : "border-transparent text-zinc-500 hover:text-zinc-300"
      }`}
    >
      <Icon className="w-3.5 h-3.5" /> {children}
    </button>
  );
}

function CapitalCascadeView({ data, error, isDemo, generatingAi, onAddActor, onGenerateAi, fractale, onReload }) {
  if (!data && !error) return <div className="text-zinc-400 text-sm flex items-center gap-2 p-6"><Loader2 className="w-4 h-4 animate-spin" /> Chargement…</div>;
  if (error && !data) return <div className="p-6"><div className="text-xs text-[#FCA5A5] p-3 border border-[#E24B4A]/30 bg-[#E24B4A]/10 rounded-md">{error}</div></div>;
  const presets = data.presets, levels = data.levels, actors = data.actors, fin = data.financement_signal, reading = data.ai_reading;
  const anchor = fractale?.anchor_sector;

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="text-xs text-zinc-400 max-w-2xl">
          {anchor ? <>Où trouver du capital, sous quelle forme, et pour financer quoi — contextualisé pour votre métier <span className="text-white">{anchor}</span>.</> :
            <>Où trouver du capital, sous quelle forme, et pour financer quoi. Générez d'abord votre fractale pour contextualiser cette lecture.</>}
        </div>
        {!isDemo && (
          <div className="flex items-center gap-2">
            <button onClick={onAddActor} className="text-xs px-3 py-1.5 rounded-md border border-white/10 hover:bg-white/5 inline-flex items-center gap-1.5" data-testid="cap-add-actor">
              <Plus className="w-3.5 h-3.5" /> Acteur
            </button>
            <button onClick={onGenerateAi} disabled={generatingAi} className="btn-primary text-xs disabled:opacity-50 inline-flex items-center gap-1.5" data-testid="cap-generate-ai">
              {generatingAi ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
              Lecture IA
            </button>
          </div>
        )}
      </div>

      {error && <div className="text-xs text-[#FCA5A5] p-3 border border-[#E24B4A]/30 bg-[#E24B4A]/10 rounded-md">{error}</div>}

      <div className="grid sm:grid-cols-3 gap-3">
        <SignalCard label="Valeur d'actifs collatéraux" value={fmtEur(fin.total_value_eur)} sub={`${fin.actif_count} actifs en Financement`} color="#378ADD" />
        <SignalCard label="Capacité finançable" value={fmtEur(fin.total_financable_eur)} sub="Selon LTV moyen" color="#1D9E75" />
        <SignalCard label="LTV global" value={`${(fin.ltv_global * 100).toFixed(1)}%`} sub="Pondéré par actif" color="#7F77DD" />
      </div>

      {reading && <AiReadingCard reading={reading} generatedAt={data.ai_reading_at} />}
      {reading && !(reading.capital_sources?.length > 0) && (
        <div className="rounded-md border border-[#EF9F27]/30 bg-[#EF9F27]/10 p-4 text-sm text-[#F3B95F]" role="note" data-testid="capital-funding-unconfirmed">
          <strong className="block mb-1">Financement non confirmé</strong>
          Aucune source de capital n’est enregistrée pour cette analyse. La cascade et les scénarios affichés sont des pistes théoriques ; ils ne confirment ni disponibilité, ni mandat, ni montant, ni accord de financement. Vérifiez les prérequis avant toute démarche.
        </div>
      )}
      {reading?.capital_sources?.length > 0 && <CapitalSourcesGrid sources={reading.capital_sources} />}
      {reading?.deal_structures?.length > 0 && <DealStructuresGrid structures={reading.deal_structures} />}
      {!reading && (
        <div className="noam-card p-6 text-center text-zinc-400 text-sm">
          <Sparkles className="w-6 h-6 text-[#1D9E75] mx-auto opacity-40" />
          <p className="mt-3">
            Aucune lecture IA générée. Cliquez sur <span className="text-[#1D9E75]">Lecture IA</span> pour obtenir :
            où trouver du capital (sources nommées), quel type de capital, et quelles formes de deal
            (obligation convertible, earn-out, vesting…) — le tout aligné sur votre métier.
          </p>
        </div>
      )}

      <div className="space-y-3" data-testid="capital-cascade">
        {presets.map((p, idx) => (
          <CascadeLevel
            key={p.key}
            preset={p}
            idx={idx}
            level={levels[p.key] || {}}
            actors={actors}
            isAccessible={reading?.accessible_levels?.includes(p.key)}
            isBlocked={reading?.blocked_levels?.find((b) => b.key === p.key)}
            isDemo={isDemo}
            onSaved={onReload}
          />
        ))}
      </div>
    </div>
  );
}

function FractaleView({ fractale, error, isDemo, generating, elapsed = 0, onGenerate }) {
  const hasLevels = (fractale?.levels?.length || 0) === 5;
  return (
    <div className="space-y-5" data-testid="fractale-view">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="max-w-3xl">
          <div className="flex items-center gap-2 text-zinc-400 text-xs">
            <Target className="w-3 h-3" />
            <span className="uppercase tracking-wider">Cascade Pareto · 5 niveaux</span>
          </div>
          <p className="text-zinc-400 text-sm mt-2">
            Remontez la chaîne des marchés qui contiennent votre métier. À chaque niveau, le marché s'élargit
            géométriquement et révèle de nouveaux acteurs, adjacences et mouvements stratégiques.
          </p>
        </div>
        {!isDemo && (
          <div className="flex items-center gap-2">
            {hasLevels && <ExportPdfButton />}
            <button
              onClick={onGenerate}
              disabled={generating}
              className="btn-primary text-xs disabled:opacity-50 inline-flex items-center gap-1.5"
              data-testid="fractale-generate-btn"
            >
              {generating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
              {hasLevels ? "Régénérer la fractale" : "Générer ma fractale"}
            </button>
          </div>
        )}
      </div>

      {error && <div className="text-xs text-[#FCA5A5] p-3 border border-[#E24B4A]/30 bg-[#E24B4A]/10 rounded-md" data-testid="fractale-error" role="alert">La génération de la fractale n’a pas abouti. Réessayez plus tard.</div>}

      {!hasLevels && !generating && (
        <div className="noam-card p-10 text-center" data-testid="fractale-empty">
          <div className="w-14 h-14 mx-auto rounded-xl bg-[#1D9E75]/10 border border-[#1D9E75]/30 flex items-center justify-center">
            <Layers className="w-6 h-6 text-[#1D9E75]" />
          </div>
          <h2 className="noam-display text-xl font-semibold mt-4">Votre fractale n'est pas encore générée</h2>
          <p className="text-zinc-400 text-sm mt-2 max-w-md mx-auto">
            NOAM analyse votre secteur et votre description pour construire une cascade de 5 niveaux,
            du métier exact (N0) au méta-marché et cycle économique (N4).
          </p>
        </div>
      )}

      {generating && (
        <div className="noam-card p-8 text-center text-zinc-400 text-sm flex flex-col items-center gap-3" data-testid="fractale-generating">
          <Loader2 className="w-6 h-6 animate-spin text-[#1D9E75]" />
          <div className="space-y-1">
            <div className="text-zinc-200 font-medium">NOAM cartographie vos 5 niveaux de marché…</div>
            <div className="text-[11px] text-zinc-500">
              Génération en arrière-plan · {elapsed}s écoulées · rafraîchissement automatique toutes les 4 s.
            </div>
            <div className="text-[11px] text-zinc-600">
              Vous pouvez naviguer ailleurs, le résultat sera là à votre retour.
            </div>
          </div>
        </div>
      )}

      {hasLevels && (
        <>
          {fractale.headline && (
            <div className="noam-card p-5 border-l-2 border-[#1D9E75]" data-testid="fractale-headline">
              <div className="text-[10px] uppercase tracking-wider text-[#1D9E75] font-semibold mb-1.5">Lecture stratégique</div>
              <div className="text-zinc-100 text-sm">{fractale.headline}</div>
            </div>
          )}

          {fractale.best_jump && (
            <div className="noam-card p-5 bg-gradient-to-br from-[#1D9E75]/8 to-transparent" data-testid="fractale-best-jump">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-[#1D9E75] font-semibold mb-2">
                <TrendingUp className="w-3 h-3" /> Meilleur saut de niveau · cette année
              </div>
              <div className="text-zinc-100 text-base font-semibold flex items-center gap-2 flex-wrap">
                <span className="text-xs px-2 py-0.5 rounded bg-white/5 border border-white/10 font-mono">N{fractale.best_jump.from_depth} → N{fractale.best_jump.to_depth}</span>
                <span>{fractale.best_jump.label}</span>
              </div>
              <p className="text-zinc-300 text-sm mt-2 leading-relaxed">{fractale.best_jump.rationale}</p>
            </div>
          )}

          <div className="space-y-3" data-testid="fractale-levels">
            {fractale.levels.map((lvl) => <FractaleLevelCard key={lvl.depth} level={lvl} />)}
          </div>

          <div className="text-[10px] text-zinc-600 text-right">
            Généré le {new Date(fractale.generated_at).toLocaleString("fr-FR")} · ancre : {fractale.anchor_sector}
          </div>
          <CockpitContextNote />
        </>
      )}
    </div>
  );
}

function CockpitContextNote() {
  return (
    <div className="noam-card p-4 border-dashed border border-white/10 bg-white/[0.02]" data-testid="cockpit-context-note">
      <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5 flex items-center gap-1.5">
        <Sparkles className="w-3 h-3" /> Signal d'ancrage utilisé par NOAM
      </div>
      <p className="text-zinc-400 text-xs leading-relaxed">
        Cette fractale n'a pas été générée à froid. NOAM a analysé votre environnement Noam — top 3 risques nets,
        actifs financement (valeur + LTV), postes RH critiques, fournisseurs critiques, positionnement,
        cascade capital remplie — pour ancrer chaque mouvement stratégique dans votre réalité opérationnelle.
        Les badges colorés sous chaque mouvement renvoient directement aux entrées correspondantes de l'environnement.
      </p>
    </div>
  );
}

const FRACTALE_COLORS = ["#1D9E75", "#378ADD", "#7F77DD", "#EF9F27", "#E24B4A"];
const FRACTALE_LABELS = ["Métier exact", "Métier-mère / filière directe", "Filière élargie", "Méta-marché sectoriel", "Cycle économique global"];

function FractaleLevelCard({ level }) {
  const color = FRACTALE_COLORS[level.depth] || "#A1A1AA";
  const widthPct = 30 + (level.depth * 17.5);
  return (
    <div className="noam-card overflow-hidden" data-testid={`fractale-level-${level.depth}`}>
      <div className="flex items-stretch">
        <div className="w-16 flex flex-col items-center justify-center py-5 shrink-0" style={{ background: `${color}1A`, borderRight: `2px solid ${color}` }}>
          <div className="text-[10px] uppercase tracking-wider font-bold" style={{ color }}>N{level.depth}</div>
          <div className="text-[9px] text-zinc-500 mt-1">{FRACTALE_LABELS[level.depth]?.split(" / ")[0]}</div>
        </div>
        <div className="flex-1 p-5">
          <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
            <div className="min-w-0">
              <h3 className="noam-display text-lg font-semibold" style={{ color }}>{level.label}</h3>
              {level.definition && <p className="text-xs text-zinc-400 mt-1 max-w-2xl">{level.definition}</p>}
            </div>
            <div className="text-right shrink-0">
              <div className="text-xs text-zinc-500">TAM estimé</div>
              <div className="noam-display text-xl font-bold" style={{ color }}>{level.tam_label || fmtEur(level.tam_eur)}</div>
              {level.size_note && <div className="text-[10px] text-zinc-500 mt-0.5 max-w-xs">{level.size_note}</div>}
            </div>
          </div>

          <div className="h-1.5 rounded-full bg-white/5 overflow-hidden mb-4">
            <div className="h-full transition-all" style={{ width: `${widthPct}%`, background: color }} />
          </div>

          <div className="grid md:grid-cols-3 gap-4 text-xs">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5 flex items-center gap-1"><Building2 className="w-3 h-3" /> Acteurs clés</div>
              <div className="space-y-1">
                {(level.actors_key || []).map((a, i) => (
                  <div key={`${a.name || "actor"}-${i}`} className="text-zinc-300">
                    <span className="text-white">{a.name}</span>
                    {a.role && <span className="text-zinc-500"> · {a.role}</span>}
                  </div>
                ))}
                {(level.actors_key || []).length === 0 && <span className="italic text-zinc-600">—</span>}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5 flex items-center gap-1"><ArrowRight className="w-3 h-3" /> Adjacences</div>
              <div className="flex flex-wrap gap-1">
                {(level.adjacencies || []).map((a, i) => (
                  <span key={`${a}-${i}`} className="text-[11px] px-2 py-0.5 rounded bg-white/5 text-zinc-200 border border-white/10">{a}</span>
                ))}
                {(level.adjacencies || []).length === 0 && <span className="italic text-zinc-600">—</span>}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5 flex items-center gap-1"><Target className="w-3 h-3" /> Mouvements recommandés</div>
              <div className="space-y-2">
                {(level.movements || []).map((m, i) => (
                  <div key={`${m.label || "mov"}-${i}`} className="space-y-1.5">
                    <div className="text-zinc-100 leading-tight">{m.label}</div>
                    <div className="text-[10px] text-zinc-500">
                      {m.rationale}
                      {m.horizon && <span className="ml-1.5 px-1.5 py-0.5 rounded font-mono bg-white/5 border border-white/10">{m.horizon}</span>}
                    </div>
                    <CockpitLinks requires={m.requires} />
                  </div>
                ))}
                {(level.movements || []).length === 0 && <span className="italic text-zinc-600">—</span>}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


function SignalCard({ label, value, sub, color }) {
  return (
    <div className="noam-card p-4">
      <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">{label}</div>
      <div className="noam-display text-2xl font-bold mt-2" style={{ color }}>{value}</div>
      <div className="text-[11px] text-zinc-400 mt-0.5">{sub}</div>
    </div>
  );
}

function AiReadingCard({ reading, generatedAt }) {
  return (
    <div className="noam-card p-5 border-l-4" style={{ borderLeftColor: "#1D9E75" }} data-testid="cap-ai-reading">
      <div className="flex items-center gap-2 mb-2">
        <Sparkles className="w-4 h-4 text-[#1D9E75]" />
        <span className="noam-overline text-[#1D9E75]">Lecture stratégique IA</span>
        <span className="ml-auto text-[10px] text-zinc-500">{generatedAt ? new Date(generatedAt).toLocaleString("fr-FR") : ""}</span>
      </div>
      {reading.headline && <div className="text-white text-base font-semibold mb-2">{reading.headline}</div>}
      {reading.narrative && <p className="text-zinc-300 text-sm leading-relaxed mb-4">{reading.narrative}</p>}
      {reading.next_actions?.length > 0 && (
        <div className="space-y-2 mt-4 pt-4 border-t border-white/5">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">Prochaines actions recommandées</div>
          {reading.next_actions.slice(0, 5).map((a, i) => (
            <div key={`${a.action || "act"}-${i}`} className="flex items-start gap-2 text-xs">
              <ArrowRight className="w-3.5 h-3.5 text-[#1D9E75] mt-0.5 shrink-0" />
              <div className="flex-1">
                <span className="text-white font-medium">{a.action}</span>
                {a.level && <span className="ml-2 text-[10px] text-zinc-500 px-1.5 py-0.5 rounded bg-white/5">{a.level}</span>}
                {a.rationale && <div className="text-zinc-400 mt-0.5">{a.rationale}</div>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CascadeLevel({ preset, idx, level, actors, isAccessible, isBlocked, isDemo, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({
    ticket_min: level.ticket_min || "",
    contrepartie: level.contrepartie || "",
    actor_ids: level.actor_ids || [],
    notes: level.notes || "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    setDraft({
      ticket_min: level.ticket_min || "", contrepartie: level.contrepartie || "",
      actor_ids: level.actor_ids || [], notes: level.notes || "",
    });
  }, [level.ticket_min, level.contrepartie, level.notes, JSON.stringify(level.actor_ids)]);

  const linkedActors = (level.actor_ids || []).map((id) => actors.find((a) => a.id === id)).filter(Boolean);
  const color = LEVEL_COLORS[preset.key] || "#7F77DD";

  const save = async () => {
    setBusy(true); setErr("");
    try {
      await api.patch(`/workspace/origination-capital/levels/${preset.key}`, draft);
      setEditing(false); onSaved();
    } catch (e) { setErr(fmtErr(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const toggleActor = (id) => {
    setDraft((d) => ({ ...d, actor_ids: d.actor_ids.includes(id) ? d.actor_ids.filter((x) => x !== id) : [...d.actor_ids, id] }));
  };

  return (
    <div className="noam-card p-0 overflow-hidden" data-testid={`cap-level-${preset.key}`}>
      <div className="flex items-stretch">
        {/* Left rail with index + color */}
        <div className="w-14 flex flex-col items-center justify-center py-5 shrink-0" style={{ background: `${color}1A`, borderRight: `2px solid ${color}` }}>
          <div className="text-[10px] uppercase tracking-wider font-bold" style={{ color }}>{idx + 1}/5</div>
        </div>
        {/* Body */}
        <div className="flex-1 p-5">
          <div className="flex items-start justify-between gap-3 mb-3">
            <div>
              <div className="flex items-center gap-2">
                <h3 className="noam-display text-lg font-semibold" style={{ color }}>{preset.label}</h3>
                {isAccessible && <span className="text-[10px] uppercase font-semibold text-[#1D9E75] bg-[#1D9E75]/10 border border-[#1D9E75]/30 px-2 py-0.5 rounded">accessible</span>}
                {isBlocked && <span className="text-[10px] uppercase font-semibold text-[#EF9F27] bg-[#EF9F27]/10 border border-[#EF9F27]/30 px-2 py-0.5 rounded" title={isBlocked.reason}>limité</span>}
              </div>
              <p className="text-[11px] text-zinc-400 mt-1">{preset.description}</p>
            </div>
            {!isDemo && !editing && (
              <button onClick={() => setEditing(true)} className="text-zinc-400 hover:text-white p-1.5 rounded hover:bg-white/5" data-testid={`cap-level-edit-${preset.key}`}>
                <Edit2 className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          {!editing ? (
            <div className="grid sm:grid-cols-3 gap-4 text-xs">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Ticket minimum</div>
                <div className="text-white">{level.ticket_min || <span className="italic text-zinc-600">— non renseigné</span>}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Contrepartie attendue</div>
                <div className="text-zinc-300">{level.contrepartie || <span className="italic text-zinc-600">— non renseignée</span>}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Acteurs liés ({linkedActors.length})</div>
                <div className="flex flex-wrap gap-1">
                  {linkedActors.length === 0 && <span className="italic text-zinc-600 text-[11px]">aucun</span>}
                  {linkedActors.map((a) => (
                    <span key={a.id} className="text-[11px] px-2 py-0.5 rounded bg-white/5 text-zinc-200 border border-white/10">{a.name}</span>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-3" data-testid={`cap-level-edit-form-${preset.key}`}>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-[11px] text-zinc-400 uppercase tracking-wider mb-1 block">Ticket minimum</label>
                  <input value={draft.ticket_min} onChange={(e) => setDraft({ ...draft, ticket_min: e.target.value })}
                    placeholder="Ex : 5 MCHF / 10 M€"
                    className="w-full bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm text-white focus:outline-none" style={{ borderColor: `${color}40` }} data-testid={`cap-ticket-${preset.key}`} />
                </div>
                <div>
                  <label className="text-[11px] text-zinc-400 uppercase tracking-wider mb-1 block">Contrepartie attendue</label>
                  <input value={draft.contrepartie} onChange={(e) => setDraft({ ...draft, contrepartie: e.target.value })}
                    placeholder="Ex : covenants, garanties, dilution…"
                    className="w-full bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm text-white focus:outline-none" style={{ borderColor: `${color}40` }} data-testid={`cap-contrepartie-${preset.key}`} />
                </div>
              </div>
              <div>
                <label className="text-[11px] text-zinc-400 uppercase tracking-wider mb-1 block">Acteurs liés (multi-sélection)</label>
                <div className="border border-white/5 rounded-md max-h-40 overflow-y-auto p-2 bg-black/20">
                  {actors.length === 0 && <div className="text-[11px] text-zinc-500 italic p-2">Aucun acteur enregistré. Cliquez sur « Acteur » en haut pour en ajouter.</div>}
                  {actors.map((a) => {
                    const checked = draft.actor_ids.includes(a.id);
                    return (
                      <label key={a.id} className="flex items-start gap-2 p-1.5 rounded hover:bg-white/5 cursor-pointer text-xs">
                        <input type="checkbox" checked={checked} onChange={() => toggleActor(a.id)} className="mt-0.5" style={{ accentColor: color }} data-testid={`cap-actor-toggle-${preset.key}-${a.id}`} />
                        <div className="flex-1 min-w-0">
                          <div className="text-white">{a.name}</div>
                          <div className="text-[10px] text-zinc-500">
                            {a.role_type && <span>{a.role_type}</span>}
                            {a.geo_level && <span> · {geoLabel(a.geo_level)}</span>}
                            {a.ticket_range && <span> · ticket {a.ticket_range}</span>}
                          </div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>
              {err && <div className="text-xs text-[#E24B4A]">{err}</div>}
              <div className="flex justify-end gap-2 pt-2">
                <button onClick={() => setEditing(false)} className="text-xs px-3 py-1.5 rounded-md border border-white/10 hover:bg-white/5">Annuler</button>
                <button onClick={save} disabled={busy} className="btn-primary text-xs disabled:opacity-50" data-testid={`cap-save-${preset.key}`}>
                  {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Enregistrer
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const ROLE_TYPES = ["Banque", "Investisseur", "Fonds PE/VC", "Family office", "Régulateur", "Partenaire", "Autre"];
// Échelle géographique canonique Noam (cf. /app/frontend/src/lib/geoScale.js)
const GEO_LEVELS = GEO_SCALE_OPTIONS;
const CAPITAL_KEYS = [
  { key: "fonds_propres", label: "Fonds propres" },
  { key: "dette_senior", label: "Dette senior" },
  { key: "dette_mezzanine", label: "Dette mezzanine" },
  { key: "capital_investissement", label: "Capital-investissement" },
  { key: "marches", label: "Marchés" },
];

function CockpitLinks({ requires }) {
  if (!requires) return null;
  const groups = [
    { key: "risks_to_mitigate", label: "Risques à mitiger", href: "/app/risques", color: "#E24B4A", icon: AlertTriangle },
    { key: "assets_to_mobilize", label: "Actifs à mobiliser", href: "/app/financement", color: "#378ADD", icon: Coins },
    { key: "hr_postes_to_strengthen", label: "Postes RH", href: "/app/rh", color: "#7F77DD", icon: Users },
    { key: "suppliers_to_derisk", label: "Fournisseurs", href: "/app/fournisseurs", color: "#EF9F27", icon: Building2 },
  ].filter(g => Array.isArray(requires[g.key]) && requires[g.key].length > 0);

  if (groups.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-1.5 pt-1.5">
      {groups.map((g) => {
        const Icon = g.icon;
        return (
          <a
            key={g.key}
            href={g.href}
            title={`${g.label} · ${requires[g.key].join(", ")}`}
            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium border transition-colors hover:bg-white/5"
            style={{ borderColor: `${g.color}55`, color: g.color }}
          >
            <Icon className="w-2.5 h-2.5" />
            {requires[g.key].slice(0, 2).join(" · ")}
            {requires[g.key].length > 2 && <span className="opacity-60">+{requires[g.key].length - 2}</span>}
          </a>
        );
      })}
    </div>
  );
}


function ActorEditModal({ actor, onClose, onSaved }) {
  const isNew = !actor?.id;
  const [form, setForm] = useState({
    name: actor.name || "",
    role_type: actor.role_type || "",
    geo_level: actor.geo_level || "",
    ticket_range: actor.ticket_range || "",
    capital_types: actor.capital_types || [],
    contact: actor.contact || "",
    notes: actor.notes || "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const toggleCap = (k) => setForm((f) => ({ ...f, capital_types: f.capital_types.includes(k) ? f.capital_types.filter((x) => x !== k) : [...f.capital_types, k] }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      if (isNew) await api.post("/workspace/capital-actors", form);
      else await api.patch(`/workspace/capital-actors/${actor.id}`, form);
      onSaved();
    } catch (er) { setErr(fmtErr(er?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    if (!actor.id || !window.confirm(`Supprimer l'acteur "${actor.name}" ?`)) return;
    setBusy(true);
    try { await api.delete(`/workspace/capital-actors/${actor.id}`); onSaved(); }
    catch (er) { setErr(fmtErr(er?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Modal onClose={onClose} maxWidth="max-w-2xl" testid="cap-actor-modal">
      <div className="flex items-start justify-between mb-5">
        <div>
          <div className="noam-overline text-[#1D9E75]">Acteur capital</div>
          <div className="noam-display text-lg font-semibold mt-0.5">{isNew ? "Nouvel acteur" : "Modifier l'acteur"}</div>
        </div>
        <button onClick={onClose} className="p-1.5 rounded hover:bg-white/5"><X className="w-4 h-4" /></button>
      </div>
      <form onSubmit={submit} className="space-y-4">
        <div>
          <label className="text-[11px] text-zinc-400 uppercase tracking-wider mb-1 block">Nom <span className="text-[#E24B4A]">*</span></label>
          <input required autoFocus value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="Ex : Banque Cantonale Vaudoise"
            className="w-full bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm text-white focus:border-[#1D9E75] focus:outline-none" data-testid="cap-actor-name" />
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="text-[11px] text-zinc-400 uppercase tracking-wider mb-1 block">Type de rôle</label>
            <select value={form.role_type} onChange={(e) => setForm({ ...form, role_type: e.target.value })}
              className="w-full bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm text-white focus:border-[#1D9E75] focus:outline-none">
              <option value="">—</option>
              {ROLE_TYPES.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[11px] text-zinc-400 uppercase tracking-wider mb-1 block">Niveau géographique</label>
            <select value={form.geo_level} onChange={(e) => setForm({ ...form, geo_level: e.target.value })}
              className="w-full bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm text-white focus:border-[#1D9E75] focus:outline-none">
              <option value="">—</option>
              {GEO_LEVELS.map((g) => <option key={g.value} value={g.value}>{g.label}</option>)}
            </select>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="text-[11px] text-zinc-400 uppercase tracking-wider mb-1 block">Ticket range</label>
            <input value={form.ticket_range} onChange={(e) => setForm({ ...form, ticket_range: e.target.value })}
              placeholder="Ex : 5-50 MCHF"
              className="w-full bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm text-white focus:border-[#1D9E75] focus:outline-none" />
          </div>
          <div>
            <label className="text-[11px] text-zinc-400 uppercase tracking-wider mb-1 block">Contact</label>
            <input value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })}
              placeholder="Email ou téléphone"
              className="w-full bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm text-white focus:border-[#1D9E75] focus:outline-none" />
          </div>
        </div>
        <div>
          <label className="text-[11px] text-zinc-400 uppercase tracking-wider mb-1 block">Types de capital apportés (multi)</label>
          <div className="flex flex-wrap gap-1.5">
            {CAPITAL_KEYS.map((c) => {
              const on = form.capital_types.includes(c.key);
              return (
                <button type="button" key={c.key} onClick={() => toggleCap(c.key)}
                  className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${on ? "border-[#1D9E75] text-[#1D9E75] bg-[#1D9E75]/10" : "border-white/10 text-zinc-400 hover:border-white/30"}`}
                  data-testid={`cap-actor-type-${c.key}`}>
                  {c.label}
                </button>
              );
            })}
          </div>
        </div>
        <div>
          <label className="text-[11px] text-zinc-400 uppercase tracking-wider mb-1 block">Notes</label>
          <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2}
            className="w-full bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm text-white focus:border-[#1D9E75] focus:outline-none" />
        </div>
        {err && <div className="text-xs text-[#E24B4A] bg-[#E24B4A]/10 border border-[#E24B4A]/30 rounded px-3 py-2">{err}</div>}
        <div className="flex justify-between pt-2 border-t border-white/5">
          {!isNew ? (
            <button type="button" onClick={remove} className="text-xs text-[#E24B4A] hover:bg-[#E24B4A]/10 px-2 py-1 rounded inline-flex items-center gap-1"><Trash2 className="w-3 h-3" /> Supprimer</button>
          ) : <div />}
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="text-xs px-3 py-1.5 rounded-md border border-white/10 hover:bg-white/5">Annuler</button>
            <button type="submit" disabled={busy} className="btn-primary text-xs disabled:opacity-50" data-testid="cap-actor-save">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} {isNew ? "Créer" : "Enregistrer"}
            </button>
          </div>
        </div>
      </form>
    </Modal>
  );
}


function ExportPdfButton() {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const onExport = async () => {
    setBusy(true); setErr(null);
    try {
      const r = await api.post("/workspace/origination/fractale/export-pdf", {}, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const cd = r.headers?.["content-disposition"] || "";
      const m = /filename="?([^";]+)"?/.exec(cd);
      const filename = m ? m[1] : "fractale_pareto.pdf";
      const a = document.createElement("a");
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      let detail = e?.response?.data;
      // When responseType=blob, error body comes as a Blob — read it
      if (detail instanceof Blob) {
        try { const txt = await detail.text(); detail = JSON.parse(txt)?.detail || txt; }
        catch (_) { detail = "Erreur d'export."; }
      }
      setErr(typeof detail === "string" ? detail : "Erreur d'export.");
    } finally { setBusy(false); }
  };
  return (
    <div className="inline-flex flex-col items-end gap-1">
      <PersonaAction action="export_pdf">
        <button
          onClick={onExport}
          disabled={busy}
          className="btn-ghost text-xs disabled:opacity-50 inline-flex items-center gap-1.5"
          data-testid="fractale-export-pdf-btn"
          title="Briefing comité PDF"
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileDown className="w-3.5 h-3.5" />}
          Export PDF
        </button>
      </PersonaAction>
      {err && <span className="text-[10px] text-[#FCA5A5]" data-testid="fractale-export-error">{err}</span>}
    </div>
  );
}

// V23.16.9 — Vue rivière persistante (3ème onglet d'Origination)
// Affiche la chaîne de valeur en SVG navigable (zoom/pan) avec drag & drop What-if
// et historique de scénarios. Réutilise <ValueChainRiver> + endpoints existants
// `/workspace/origination/value-chain*` + `/workspace/origination/value-chain/scenarios*`.
// V23.16.x — Sources de capital nommées (générées par la Lecture IA)
function CapitalSourcesGrid({ sources }) {
  return (
    <div className="space-y-3" data-testid="cap-sources-grid">
      <div className="flex items-center gap-2">
        <Building2 className="w-4 h-4 text-[#378ADD]" />
        <span className="noam-overline text-[#378ADD]">Où trouver du capital — sources identifiées pour votre métier</span>
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        {sources.slice(0, 10).map((s, i) => (
          <div key={`${s.name || "src"}-${i}`} className="noam-card p-4" data-testid={`cap-source-${i}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="text-sm text-white font-semibold">{s.name}</div>
              {s.type && <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#378ADD]/15 text-[#378ADD] border border-[#378ADD]/30 shrink-0">{s.type}</span>}
            </div>
            <div className="flex items-center gap-2 mt-1.5 text-[10px] text-zinc-500 flex-wrap">
              {s.geo && <span className="px-1.5 py-0.5 rounded bg-white/5 border border-white/10">{s.geo}</span>}
              {s.ticket_range && <span className="px-1.5 py-0.5 rounded bg-white/5 border border-white/10">{s.ticket_range}</span>}
            </div>
            {s.why_relevant && <p className="text-xs text-zinc-300 mt-2 leading-relaxed">{s.why_relevant}</p>}
            {s.how_to_approach && (
              <div className="mt-2 pt-2 border-t border-white/5 text-[11px] text-zinc-400 flex items-start gap-1.5">
                <ArrowRight className="w-3 h-3 text-[#1D9E75] mt-0.5 shrink-0" />
                <span>{s.how_to_approach}</span>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// V23.16.x — Formes de deal (obligation convertible, earn-out, vesting…)
function DealStructuresGrid({ structures }) {
  return (
    <div className="space-y-3" data-testid="cap-deal-structures">
      <div className="flex items-center gap-2">
        <Coins className="w-4 h-4 text-[#EF9F27]" />
        <span className="noam-overline text-[#EF9F27]">Quelles formes de deal — structures adaptées à votre situation</span>
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        {structures.slice(0, 8).map((d, i) => (
          <div key={`${d.name || "deal"}-${i}`} className="noam-card p-4 border-l-2 border-[#EF9F27]/40" data-testid={`cap-deal-${i}`}>
            <div className="text-sm text-white font-semibold">{d.name}</div>
            {d.when_to_use && (
              <div className="mt-2 text-[11px]">
                <span className="text-[10px] uppercase tracking-wider text-zinc-500">Quand l'utiliser</span>
                <p className="text-zinc-300 mt-0.5">{d.when_to_use}</p>
              </div>
            )}
            {d.impact && (
              <div className="mt-2 text-[11px]">
                <span className="text-[10px] uppercase tracking-wider text-zinc-500">Impact</span>
                <p className="text-zinc-400 mt-0.5">{d.impact}</p>
              </div>
            )}
            <div className="flex items-start gap-2 mt-2 pt-2 border-t border-white/5">
              {d.urgency && <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#EF9F27]/10 text-[#EF9F27] border border-[#EF9F27]/25 shrink-0">{d.urgency}</span>}
              {d.action && <span className="text-[11px] text-zinc-300">{d.action}</span>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function RiviereTab() {
  const [data, setData] = useState({ nodes: [], is_empty: true });
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [isWhatIf, setIsWhatIf] = useState(false);
  const [scenarios, setScenarios] = useState([]);

  const load = async () => {
    try {
      const [vc, sc] = await Promise.all([
        api.get("/workspace/origination/value-chain"),
        api.get("/workspace/origination/value-chain/scenarios").catch(() => ({ data: { scenarios: [] } })),
      ]);
      setData(vc.data || { nodes: [], is_empty: true });
      setScenarios((sc.data?.scenarios) || []);
      setError("");
    } catch (e) {
      setError(e?.response?.data?.detail || "Chargement impossible.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const onGenerate = async () => {
    setGenerating(true); setError("");
    try {
      await api.post("/workspace/origination/value-chain/generate", {});
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || "Génération impossible.");
    } finally {
      setGenerating(false);
    }
  };

  const onMoveNode = async (nodeId, newFlow) => {
    try {
      await api.patch(`/workspace/origination/value-chain/nodes/${nodeId}`, { flow_position: newFlow });
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || "Déplacement impossible.");
    }
  };

  const selfNode = (data.nodes || []).find((n) => n.is_self);
  const selectedNode = (data.nodes || []).find((n) => n.id === selectedId);

  if (loading) {
    return (
      <div className="noam-card p-12 text-center text-zinc-500 text-sm">
        <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Chargement de la rivière…
      </div>
    );
  }

  if (data.is_empty || (data.nodes || []).length === 0) {
    return (
      <div className="noam-card p-12 text-center space-y-4" data-testid="riviere-empty">
        <Waves className="w-10 h-10 text-[#7F77DD] mx-auto" />
        <div>
          <h3 className="text-lg font-semibold text-zinc-100">Visualisez votre chaîne de valeur</h3>
          <p className="text-xs text-zinc-500 mt-2 max-w-md mx-auto">
            NOAM analyse votre Positionnement et votre Annuaire pour identifier les acteurs en amont
            (régulateurs, infrastructures, fournisseurs) et en aval (clients, marchés, prescripteurs)
            de votre activité.
          </p>
        </div>
        <button
          onClick={onGenerate}
          disabled={generating}
          className="text-xs px-4 py-2 rounded bg-[#7F77DD] text-white hover:bg-[#7F77DD]/80 inline-flex items-center gap-1.5 disabled:opacity-50"
          data-testid="riviere-generate-btn"
        >
          {generating ? <><Loader2 className="w-3 h-3 animate-spin" /> Génération…</> : <><Sparkles className="w-3 h-3" /> Générer ma chaîne de valeur</>}
        </button>
        {error && <div className="text-xs text-[#FCA5A5]" role="alert">L’action n’a pas abouti. Réessayez dans quelques instants.</div>}
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="riviere-tab" id="riviere-capture">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-[#7F77DD] font-semibold inline-flex items-center gap-1.5">
            <Waves className="w-3 h-3" /> Rivière de valeur · navigable
          </div>
          <h3 className="text-sm text-zinc-100 mt-0.5">
            {(data.nodes || []).length} acteurs · {scenarios.length} scénarios sauvegardés
          </h3>
        </div>
        <div className="flex items-center gap-2 text-[11px]">
          <span className="text-zinc-500">Wheel = zoom · drag = pan</span>
          <PitchCaptureButton
            targetId="riviere-capture"
            filename="noam-vue-riviere"
            docTitle="Rivière de valeur"
            docSubtitle="Le courant systémique qui porte votre activité — régulateurs, infrastructures, fournisseurs, clients et prescripteurs."
            threeQEndpoint="/workspace/sources-capital/top-priority"
          />
          <button
            onClick={() => setIsWhatIf((v) => !v)}
            className={`px-2.5 py-1 rounded border text-[10px] inline-flex items-center gap-1 ${isWhatIf ? "bg-[#EF9F27]/15 border-[#EF9F27]/40 text-[#EF9F27]" : "bg-white/[0.03] border-white/10 text-zinc-400 hover:text-zinc-100"}`}
            data-testid="riviere-whatif-toggle"
          >
            <Target className="w-3 h-3" /> Mode What-if {isWhatIf ? "ON" : "OFF"}
          </button>
          <button
            onClick={load}
            className="px-2 py-1 rounded text-zinc-400 hover:text-zinc-100 inline-flex items-center gap-1 text-[10px]"
            title="Recharger"
          >
            <RefreshCw className="w-3 h-3" />
          </button>
        </div>
      </div>

      <div className="noam-card p-4">
        <ValueChainRiver
          nodes={data.nodes}
          selectedId={selectedId}
          onSelectNode={(n) => setSelectedId(n.id)}
          onResetToSelf={() => setSelectedId(selfNode?.id)}
          onMoveNode={isWhatIf ? onMoveNode : undefined}
          isWhatIfMode={isWhatIf}
          height={420}
          testIdPrefix="riviere"
        />
      </div>

      {selectedNode && (
        <div className="noam-card p-4 space-y-2" data-testid="riviere-node-detail">
          <div className="flex items-center justify-between">
            <div className="inline-flex items-center gap-2">
              <span className="w-2 h-2 rounded-full" style={{ background: selectedNode.is_self ? "#EF9F27" : (selectedNode.flow_position < 50 ? "#7F77DD" : "#1D9E75") }} />
              <h4 className="text-sm font-semibold text-zinc-100">{selectedNode.name}</h4>
              {selectedNode.is_self && <span className="text-[9px] text-[#EF9F27]">★ MOI</span>}
            </div>
            <button onClick={() => setSelectedId(null)} className="text-zinc-500 hover:text-zinc-100"><X className="w-3.5 h-3.5" /></button>
          </div>
          {selectedNode.role && <div className="text-xs text-zinc-300"><span className="text-zinc-500">Rôle :</span> {selectedNode.role}</div>}
          <div className="grid grid-cols-3 gap-2 text-[11px]">
            <div className="rounded bg-white/[0.03] p-2">
              <div className="text-zinc-500 text-[9px] uppercase">Position flux</div>
              <div className="text-zinc-100 font-semibold">{selectedNode.flow_position}/100</div>
              <div className="text-zinc-500 text-[10px]">{selectedNode.flow_position < 50 ? "amont" : "aval"}</div>
            </div>
            <div className="rounded bg-white/[0.03] p-2">
              <div className="text-zinc-500 text-[9px] uppercase">Capture valeur</div>
              <div className="text-zinc-100 font-semibold">{selectedNode.capture_pct ?? "—"}%</div>
            </div>
            <div className="rounded bg-white/[0.03] p-2">
              <div className="text-zinc-500 text-[9px] uppercase">Poids négo</div>
              <div className="text-zinc-100 font-semibold">{selectedNode.negotiation_weight_pct ?? "—"}%</div>
            </div>
          </div>
          {selectedNode.notes && <div className="text-[11px] text-zinc-400 leading-snug bg-white/[0.02] rounded p-2 border border-white/5">{selectedNode.notes}</div>}
        </div>
      )}

      {error && <div className="text-xs text-[#FCA5A5]" role="alert">L’action n’a pas abouti. Réessayez dans quelques instants.</div>}
    </div>
  );
}

